// The Hebrew calendar the person card needs: where a stored Hebrew date falls in a given year, and what
// its months are called. A TypeScript port of VMR `people_service/HebrewDates.java` (the server's one place
// that knows the calendar's rules), so the card shows the SAME next occurrence the date producer fires on.
//
// The calendar itself comes from the platform's ICU through `Intl` (`-u-ca-hebrew`) — no library: every
// browser and Node ship it, and the server's rules sit on ICU too. The two rules are hebcal's
// (plan §1.2, decided 2026-10-16), copied rule for rule from `HebrewDates.java`:
//  - birthday / anniversary (every kind but MEMORIAL): a day the target year lacks is POSTPONED —
//    30 Cheshvan / 30 Kislev in a 29-day year → the 1st of the next month; 30 Adar I in a common year →
//    1 Nisan. ADAR and ADAR_II land in the year's LAST Adar; ADAR_I lands in Adar I, or Adar in a common year.
//  - yahrzeit (MEMORIAL): 30 Adar I in a common year → 30 Shevat; ADAR_II → the last Adar; ADAR / ADAR_I →
//    Adar I in a leap year, Adar in a common one; 30 Cheshvan / Kislev depend on the first anniversary
//    (death year + 1) when the year is known.
//
// iOS (`Calendar(identifier: .hebrew)`) and Android (`android.icu.util.HebrewCalendar`) port the same file.

export type HebrewMonth =
  | 'TISHREI'
  | 'CHESHVAN'
  | 'KISLEV'
  | 'TEVET'
  | 'SHEVAT'
  | 'ADAR'
  | 'ADAR_I'
  | 'ADAR_II'
  | 'NISAN'
  | 'IYAR'
  | 'SIVAN'
  | 'TAMMUZ'
  | 'AV'
  | 'ELUL';

/** Every stored month, in the order a picker lists them (a leap year's order, with plain Adar beside Adar I/II). */
export const HEBREW_MONTHS: HebrewMonth[] = [
  'TISHREI',
  'CHESHVAN',
  'KISLEV',
  'TEVET',
  'SHEVAT',
  'ADAR',
  'ADAR_I',
  'ADAR_II',
  'NISAN',
  'IYAR',
  'SIVAN',
  'TAMMUZ',
  'AV',
  'ELUL',
];

/** The longest each month ever runs. A day past it is not a date (Cheshvan/Kislev are 29 or 30 by year). */
export const HEBREW_MONTH_MAX_DAYS: Record<HebrewMonth, number> = {
  TISHREI: 30,
  CHESHVAN: 30,
  KISLEV: 30,
  TEVET: 29,
  SHEVAT: 30,
  ADAR: 29,
  ADAR_I: 30,
  ADAR_II: 29,
  NISAN: 30,
  IYAR: 29,
  SIVAN: 30,
  TAMMUZ: 29,
  AV: 30,
  ELUL: 29,
};

export interface CivilDate {
  year: number;
  month: number; // 1-12
  day: number;
}

interface HebrewDay {
  year: number;
  month: HebrewMonth;
  day: number;
}

const DAY_MS = 86_400_000;

/** ICU's English month names → the stored enum. Alternate spellings are accepted so an ICU update cannot break it. */
const ICU_NAME_TO_MONTH: Record<string, HebrewMonth | 'ADAR_PLAIN'> = {
  tishri: 'TISHREI',
  tishrei: 'TISHREI',
  heshvan: 'CHESHVAN',
  cheshvan: 'CHESHVAN',
  marheshvan: 'CHESHVAN',
  kislev: 'KISLEV',
  tevet: 'TEVET',
  teveth: 'TEVET',
  shevat: 'SHEVAT',
  shvat: 'SHEVAT',
  'adar i': 'ADAR_I',
  'adar ii': 'ADAR_II',
  adar: 'ADAR_PLAIN',
  nisan: 'NISAN',
  iyar: 'IYAR',
  sivan: 'SIVAN',
  tamuz: 'TAMMUZ',
  tammuz: 'TAMMUZ',
  av: 'AV',
  elul: 'ELUL',
};

let englishHebrew: Intl.DateTimeFormat | null = null;
function englishHebrewFormat(): Intl.DateTimeFormat {
  englishHebrew ??= new Intl.DateTimeFormat('en-u-ca-hebrew', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
  return englishHebrew;
}

/** Years 3, 6, 8, 11, 14, 17 and 19 of the 19-year Metonic cycle carry Adar I. */
export function isHebrewLeapYear(hebrewYear: number): boolean {
  return (((7 * hebrewYear + 1) % 19) + 19) % 19 < 7;
}

function utc(date: CivilDate): number {
  return Date.UTC(date.year, date.month - 1, date.day);
}

function civil(ms: number): CivilDate {
  const d = new Date(ms);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

function hebrewAt(ms: number): HebrewDay {
  const parts = englishHebrewFormat().formatToParts(new Date(ms));
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  const year = Number(get('year').replace(/\D/g, ''));
  const mapped = ICU_NAME_TO_MONTH[get('month').trim().toLowerCase()];
  if (!mapped) throw new Error(`hebrew-calendar: unknown ICU month "${get('month')}"`);
  const month: HebrewMonth = mapped === 'ADAR_PLAIN' ? (isHebrewLeapYear(year) ? 'ADAR_II' : 'ADAR') : mapped;
  return { year, month, day: Number(get('day').replace(/\D/g, '')) };
}

/** The Hebrew day a civil date is (the daytime date — a stored Hebrew date is never second-guessed for sunset). */
export function hebrewDateOf(date: CivilDate): HebrewDay {
  return hebrewAt(utc(date));
}

interface YearTable {
  /** UTC ms of 1 Tishrei. */
  start: number;
  months: { month: HebrewMonth; days: number }[];
}

const yearTables = new Map<number, YearTable>();

/** The months of one Hebrew year and their lengths, read off ICU once per year. */
function yearTable(hebrewYear: number): YearTable {
  const cached = yearTables.get(hebrewYear);
  if (cached) return cached;
  // 1 Tishrei of year Y falls in September or early October of civil year Y - 3761.
  let ms = Date.UTC(hebrewYear - 3761, 7, 25);
  for (let i = 0; i < 60; i += 1) {
    const h = hebrewAt(ms);
    if (h.year === hebrewYear && h.month === 'TISHREI' && h.day === 1) break;
    ms += DAY_MS;
  }
  const start = ms;
  const months: { month: HebrewMonth; days: number }[] = [];
  for (;;) {
    const h = hebrewAt(ms);
    if (h.year !== hebrewYear) break;
    const last = months[months.length - 1];
    if (last && last.month === h.month) last.days = h.day;
    else months.push({ month: h.month, days: h.day });
    ms += DAY_MS;
  }
  const table = { start, months };
  yearTables.set(hebrewYear, table);
  return table;
}

function daysIn(hebrewYear: number, month: HebrewMonth): number {
  return yearTable(hebrewYear).months.find((entry) => entry.month === month)?.days ?? 0;
}

/** Whether `day` of `month` exists in `hebrewYear` (Adar I/II only in a leap year, plain Adar only in a common one). */
export function hebrewDayExists(hebrewYear: number, month: HebrewMonth, day: number): boolean {
  return Number.isInteger(day) && day >= 1 && day <= daysIn(hebrewYear, month);
}

/** The civil date of a Hebrew day that exists in its year. */
export function hebrewToCivil(hebrewYear: number, month: HebrewMonth, day: number): CivilDate {
  const table = yearTable(hebrewYear);
  let offset = 0;
  for (const entry of table.months) {
    if (entry.month === month) return civil(table.start + (offset + day - 1) * DAY_MS);
    offset += entry.days;
  }
  throw new Error(`hebrew-calendar: ${month} does not occur in ${hebrewYear}`);
}

function postponed(year: number, month: HebrewMonth, day: number): HebrewDay {
  if (day === 30 && daysIn(year, month) === 29) {
    return { year, month: month === 'CHESHVAN' ? 'KISLEV' : 'TEVET', day: 1 };
  }
  return { year, month, day };
}

function birthday(year: number, month: HebrewMonth, day: number): HebrewDay {
  const leap = isHebrewLeapYear(year);
  switch (month) {
    case 'ADAR':
    case 'ADAR_II':
      return { year, month: leap ? 'ADAR_II' : 'ADAR', day };
    case 'ADAR_I':
      if (leap) return { year, month: 'ADAR_I', day };
      return day === 30 ? { year, month: 'NISAN', day: 1 } : { year, month: 'ADAR', day };
    case 'CHESHVAN':
    case 'KISLEV':
      return postponed(year, month, day);
    default:
      return { year, month, day };
  }
}

function yahrzeit(year: number, month: HebrewMonth, day: number, deathYear: number | null): HebrewDay {
  const leap = isHebrewLeapYear(year);
  if ((month === 'CHESHVAN' || month === 'KISLEV') && day === 30 && deathYear !== null && daysIn(deathYear + 1, month) === 29) {
    // The first anniversary had no 30th, so it was the month's last day — and so is every one after.
    return { year, month, day: daysIn(year, month) };
  }
  switch (month) {
    case 'ADAR_II':
      return { year, month: leap ? 'ADAR_II' : 'ADAR', day };
    case 'ADAR':
    case 'ADAR_I':
      if (leap) return { year, month: 'ADAR_I', day };
      return day === 30 ? { year, month: 'SHEVAT', day: 30 } : { year, month: 'ADAR', day };
    case 'CHESHVAN':
    case 'KISLEV':
      return postponed(year, month, day);
    default:
      return { year, month, day };
  }
}

/** Where a stored Hebrew date is observed in `hebrewYear` (VMR `HebrewDates.observed`). */
export function observedHebrewDay(
  hebrewYear: number,
  month: HebrewMonth,
  day: number,
  storedYear: number | null,
  memorial: boolean,
): HebrewDay {
  return memorial ? yahrzeit(hebrewYear, month, day, storedYear) : birthday(hebrewYear, month, day);
}

/**
 * The first civil day on or after `from` the stored Hebrew date is observed, with the Hebrew year it is
 * observed in (for "turns N"). Years before the stored year are skipped, and so is the year of death itself
 * for a yahrzeit — hebcal's own bounds, as `HebrewDates.occurrences`.
 */
export function nextHebrewOccurrence(
  month: HebrewMonth,
  day: number,
  storedYear: number | null,
  memorial: boolean,
  from: CivilDate,
): { date: CivilDate; hebrewYear: number } | null {
  const fromMs = utc(from);
  const first = hebrewDateOf(from).year - 1;
  let best: { date: CivilDate; hebrewYear: number; ms: number } | null = null;
  for (let year = first; year <= first + 3; year += 1) {
    if (storedYear !== null && (memorial ? year <= storedYear : year < storedYear)) continue;
    const observed = observedHebrewDay(year, month, day, storedYear, memorial);
    const date = hebrewToCivil(observed.year, observed.month, observed.day);
    const ms = utc(date);
    if (ms >= fromMs && (!best || ms < best.ms)) best = { date, hebrewYear: year, ms };
  }
  return best ? { date: best.date, hebrewYear: best.hebrewYear } : null;
}

/**
 * A Hebrew month's name in the reader's language, from ICU (`he` → "אדר א׳", `en` → "Adar I"). Read off a
 * day known to fall in that month: 5784 is a leap year (Adar I / Adar II), 5785 a common one (plain Adar).
 */
export function hebrewMonthName(month: HebrewMonth, locale: string): string {
  const year = month === 'ADAR' ? 5785 : 5784;
  const date = hebrewToCivil(year, month, 1);
  try {
    return new Intl.DateTimeFormat(`${locale}-u-ca-hebrew`, { month: 'long', timeZone: 'UTC' }).format(new Date(utc(date)));
  } catch {
    return new Intl.DateTimeFormat('en-u-ca-hebrew', { month: 'long', timeZone: 'UTC' }).format(new Date(utc(date)));
  }
}
